import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import { envUtils } from './core';
import {
  isDesktopBridgeRuntime,
  desktopBridgeGetAccessToken,
  desktopBridgeRefreshAccessToken,
} from './core/auth/desktop-auth-bridge';

const BROWSER_AUTH_EXPIRED_EVENT = 'revolt:auth-expired';
const AUTH_EXPIRY_EXCLUDED_PATHS = [
  '/api/account/login',
  '/api/account/register',
  '/api/account/forgot-password',
  '/api/account/reset-password',
  '/api/account/verify-email',
  '/api/account/status',
  '/api/account/logout',
  '/api/account/desktop/login',
  '/api/account/desktop/refresh',
  '/api/account/desktop/status',
  '/api/account/desktop/logout',
  // /me returns 401 when not logged in; public pages (pricing, etc.) call it to check membership
  '/api/account/me',
  '/api/legal/',
];

function getRequestPath(input) {
  const rawUrl = typeof input === 'string' ? input : input?.url || '';

  try {
    return new URL(rawUrl, window.location.origin).pathname;
  } catch {
    return rawUrl;
  }
}

function shouldDispatchBrowserAuthExpired(pathname) {
  return !AUTH_EXPIRY_EXCLUDED_PATHS.some((prefix) => pathname.startsWith(prefix));
}

async function isBrowserAuthExpiryResponse(response, pathname) {
  if (response.status !== 401 || !shouldDispatchBrowserAuthExpired(pathname)) {
    return false;
  }

  try {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return false;
    }

    const payload = await response.clone().json();
    const normalizedError = String(payload?.error || payload?.message || '').toLowerCase();

    return (
      payload?.authenticated === false ||
      normalizedError.includes('authentication required') ||
      normalizedError.includes('user not found')
    );
  } catch {
    return false;
  }
}

if (typeof window !== "undefined") {
  const nativeFetch = window.fetch.bind(window);
  let browserAuthExpiryPending = false;

  window.fetch = async (input, init = {}) => {
    const pathname = getRequestPath(input);
    const isApiRequest = pathname.startsWith("/api");
    const isDesktopRuntime = isDesktopBridgeRuntime();

    if (!isApiRequest) {
      return nativeFetch(input, init);
    }

    const initialHeaders = new Headers(init?.headers || (input instanceof Request ? input.headers : {}));
    initialHeaders.set('X-Revolt-Client-Environment', envUtils.device.getEnvironmentType());

    if (isDesktopRuntime && !initialHeaders.has("Authorization")) {
      const token = await desktopBridgeGetAccessToken().catch(() => null);
      if (token) {
        initialHeaders.set("Authorization", `Bearer ${token}`);
      }
    }

    const requestInit = {
      ...init,
      credentials: "include",
      headers: initialHeaders,
    };

    let response = await nativeFetch(input, requestInit);
    if (isDesktopRuntime && response.status !== 401) {
      return response;
    }

    if (isDesktopRuntime) {
      const refreshedToken = await desktopBridgeRefreshAccessToken().catch(() => null);
      if (!refreshedToken) {
        return response;
      }

      const retryHeaders = new Headers(initialHeaders);
      retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);
      return nativeFetch(input, {
        ...requestInit,
        headers: retryHeaders,
      });
    }

    if (await isBrowserAuthExpiryResponse(response, pathname)) {
      if (!browserAuthExpiryPending) {
        browserAuthExpiryPending = true;
        window.dispatchEvent(
          new CustomEvent(BROWSER_AUTH_EXPIRED_EVENT, {
            detail: {
              requestPath: pathname,
            },
          })
        );
        window.setTimeout(() => {
          browserAuthExpiryPending = false;
        }, 250);
      }
    }

    return response;
  };
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);
